import * as _ from 'lodash-es';
import type { Parchment, Range } from 'quill/core';
import type { ReactNode } from 'react';
import React, { createContext, useContext, useMemo, useReducer } from 'react';
import type Quill from './QuillEditor';
import HOCWithDisplayName from '../utils/HOCWithDisplayName';

const NO_CONTEXT_ERROR = 'Could not find Quill context value.';

export interface SelectionContextValue {
  formats: Record<string, unknown>;
  range: Range | null;
}

interface InstanceContextValue {
  quill: Quill | null;
  ancestorQuill: Quill | null;
}

type ReadOnlyContextValue = boolean;

interface StateContextValue {
  isEditingLink: boolean;
  activeBlot: Parchment.Blot | null;
}

interface Mutations {
  editorMounted(quill: Quill): void;
  editorUnmounted(): void;
  localSelection(selection: {
    formats: Record<string, unknown>;
    range: Range | null;
  }): void;
  editingLinkChange(editing: boolean): void;
  readOnlyChange(readOnly: boolean): void;
  onActiveBlotChange(blot: Parchment.Blot | null): void;
}

const SelectionContext = createContext<SelectionContextValue | null>(null);
const InstanceContext = createContext<InstanceContextValue | null>(null);
const ReadOnlyContext = createContext<ReadOnlyContextValue | null>(null);
const StateContext = createContext<StateContextValue | null>(null);
const MutationsContext = createContext<Mutations | null>(null);

const useForcedContext = <T,>(context: React.Context<T | null>) => {
  const value = useContext(context);
  if (value == null) throw new Error(NO_CONTEXT_ERROR);

  return value;
};

export const useQuill = () => {
  return useForcedContext(InstanceContext);
};

export const useQuillReadOnly = () => {
  return useForcedContext(ReadOnlyContext);
};

export const useQuillSelection = () => {
  return useForcedContext(SelectionContext);
};

export const useQuillState = () => {
  return useForcedContext(StateContext);
};

export const useQuillMutations = () => {
  return useForcedContext(MutationsContext);
};

interface State extends InstanceContextValue, StateContextValue {
  selection: SelectionContextValue;
  readOnly: ReadOnlyContextValue;
}

const getInitialState = (): State => ({
  ancestorQuill: null,
  quill: null,
  selection: {
    formats: {},
    range: null,
  },
  isEditingLink: false,
  readOnly: false,
  activeBlot: null,
});

type Action =
  | { type: 'editorMounted'; value: Quill }
  | { type: 'editorUnmounted' }
  | {
      type: 'localSelection';
      value: {
        range: Range | null;
        formats: Record<string, unknown>;
      };
    }
  | { type: 'editingLinkChange'; value: boolean }
  | { type: 'readOnlyChange'; value: boolean }
  | {
      type: 'activeBlotChange';
      value: Parchment.Blot | null;
    };

const quillReducer = (state: State, action: Action) => {
  switch (action.type) {
    case 'editorMounted':
      return { ...state, quill: action.value };
    case 'editorUnmounted':
      return getInitialState();
    case 'localSelection': {
      const hasRangeChanged = state.selection.range !== action.value.range;
      return {
        ...state,
        selection: { ...action.value },
        isEditingLink: hasRangeChanged ? false : state.isEditingLink,
      };
    }
    case 'editingLinkChange':
      return state.isEditingLink === action.value
        ? state
        : { ...state, isEditingLink: action.value };
    case 'readOnlyChange':
      return state.readOnly === action.value
        ? state
        : { ...state, readOnly: action.value };
    case 'activeBlotChange':
      return state.activeBlot === action.value
        ? state
        : { ...state, activeBlot: action.value };
    default:
      return state;
  }
};

// eslint-disable-next-line import/no-unused-modules -- expose for testing
export interface QuillContextProps {
  children: ReactNode;
  initialValues?: Partial<State>;
}

interface InnerQuillContextProps extends QuillContextProps {
  ancestorQuill: Quill | null;
}

const InnerQuillContext = ({
  ancestorQuill,
  children,
  initialValues,
}: InnerQuillContextProps) => {
  const [state, dispatch] = useReducer(
    quillReducer,
    _.merge(getInitialState(), initialValues),
  );

  const mutations = useMemo<Mutations>(
    () => ({
      editorMounted: (quill) => {
        dispatch({ type: 'editorMounted', value: quill });
      },
      editorUnmounted: () => {
        dispatch({ type: 'editorUnmounted' });
      },
      localSelection: ({ range, formats }: SelectionContextValue) => {
        dispatch({ type: 'localSelection', value: { range, formats } });
      },
      editingLinkChange: (editing: boolean) => {
        dispatch({ type: 'editingLinkChange', value: editing });
      },
      readOnlyChange: (readOnly: boolean) => {
        dispatch({ type: 'readOnlyChange', value: readOnly });
      },
      onActiveBlotChange: (blot: Parchment.Blot | null) => {
        dispatch({ type: 'activeBlotChange', value: blot });
      },
    }),
    [dispatch],
  );

  const instanceContextValue = useMemo(
    () => ({
      quill: state.quill,
      ancestorQuill: ancestorQuill ?? state.quill,
    }),
    [state.quill, ancestorQuill],
  );

  const stateContextValue = useMemo(
    () => ({
      isEditingLink: state.isEditingLink,
      activeBlot: state.activeBlot,
    }),
    [state.isEditingLink, state.activeBlot],
  );

  return (
    <SelectionContext.Provider value={state.selection}>
      <InstanceContext.Provider value={instanceContextValue}>
        <ReadOnlyContext.Provider value={state.readOnly}>
          <StateContext.Provider value={stateContextValue}>
            <MutationsContext.Provider value={mutations}>
              {children}
            </MutationsContext.Provider>
          </StateContext.Provider>
        </ReadOnlyContext.Provider>
      </InstanceContext.Provider>
    </SelectionContext.Provider>
  );
};

const QuillContext = ({ children, initialValues }: QuillContextProps) => (
  <InstanceContext.Consumer>
    {(parentValue) => (
      <InnerQuillContext
        ancestorQuill={parentValue ? parentValue.quill : null}
        initialValues={initialValues}
      >
        {children}
      </InnerQuillContext>
    )}
  </InstanceContext.Consumer>
);

export const withQuillContextProvider = <P extends object>(
  Component: React.ComponentType<P>,
) => {
  const WithQuillContextProvider = (props: P) => (
    <QuillContext>
      <Component {...props} />
    </QuillContext>
  );
  return HOCWithDisplayName('withQuillContextProvider')(
    WithQuillContextProvider,
  );
};

export default QuillContext;
